<template>
  <SubLayout
      :pageTitle="title"
      :pageBreadcrumbs="breadcrumbs"
      :loading="isLoading"
  >
    <template #layoutContent>
      <Loader :isLoading="isLoading">
        <EstimateForm
            :estimate="estimate"
            :submit-form="!isEdit ? createEstimate : updateEstimate"
            :submitting="submitting"
        />
      </Loader>
    </template>
  </SubLayout>
</template>

<script setup>

import SubLayout from "@/views/Layouts/partials/SubLayout.vue";
import {useRoute, useRouter} from "vue-router";
import {useStore} from "vuex";
import {computed, onMounted, ref, watch} from "vue";
import EstimateForm from "@/Modules/CRM/components/Estimate/EstimateForm.vue";
import {addDay, formatDate} from "@/mixins/Helpers";
import Loader from "@/components/Loader";

const route = useRoute()
const router = useRouter()
const store = useStore()
const title = ref("breadcrumbs.estimate.title")
const breadcrumbs = ref([
  {
    name: 'breadcrumbs.estimate.list',
    route: 'estimates',
    params: {}
  },
  {
    name: route.params.id ? 'breadcrumbs.estimate.edit' : 'breadcrumbs.estimate.new',
    route: route.params.id ? 'estimates-edit' : 'estimates-create',
    params: route.params.id ? { id: route.params.id } : null
  }
])

const isLoading = ref(true)
const submitting = ref(false)

const today = new Date();
const day = String(today.getDate()).padStart(2, '0');
const month = String(today.getMonth() + 1).padStart(2, '0');
const year = today.getFullYear();
const formattedStartDate = `${day}-${month}-${year}`;

const estimate = ref({
  id: null,
  estimate_date: formattedStartDate,
  due_date: addDay(formattedStartDate, 30),
  items: [{
    product_id: '',
    quantity: 1,
    price: 0,
    description: '',
    discount_type: 'percent',
    discount_value: 0,
    taxes: [],
    total: 0
  }],
  taxes: [],
  notes: '',
  validated: '',
  payment_condition_id: '',
  exchange_rate: 1,
  contact_id: '',
  currency_id: '',
  opportunity_id: '',
  payment_type_id: ''
})

const isEdit = computed(() => !!route.params.id)
const currentEstimate = computed(() => store.getters['estimates/currentEstimate'])
const contacts = computed(() => store.getters['contacts/getContacts'])

watch(() => currentEstimate.value, (newValue) => {
  if(newValue.id && route.params.id) {
    estimate.value.id = newValue.id
    estimate.value.estimate_date = formatDate(newValue.estimate_date)
    estimate.value.items = newValue.items?.map(el => ({
      product_id: el.product_sale_element_id,
      quantity: el.quantity,
      price: el.price,
      description: el.description,
      discount_type: el.discount_type,
      discount_value: el.discount_value,
      taxes: el.taxes?.map(el => el.id),
    }))
    estimate.value.taxes = []
    estimate.value.taxes = newValue.taxes?.map(el => el.taxe_id)
    estimate.value.exchange_rate = newValue?.exchange_rate
    estimate.value.currency_id =  newValue?.currency?.id
    estimate.value.contact_id = newValue?.contact?.id
    estimate.value.due_date =  formatDate(newValue.due_date)
    estimate.value.notes = newValue.notes
    estimate.value.validated = newValue.payment_status
    estimate.value.opportunity_id = newValue.opportunity_id
    estimate.value.payment_type_id = newValue?.paymentType?.id
  } else {
    estimate.value = null
    estimate.value = {
      estimate_date: formattedStartDate,
      due_date: addDay(formattedStartDate, 30),
      items: [{
        product_id: '',
        quantity: 1,
        price: 0,
        description: '',
        discount_type: 'percent',
        discount_value: 0,
        taxes: [],
        total: 0
      }],
      taxes: [],
      notes: '',
      validated: '',
      payment_condition_id: '',
      exchange_rate: 1,
      contact_id: '',
      currency_id: '',
      payment_type_id: '',
      opportunity_id: ''
    }
  }

}, {immediate: true})

const createEstimate = () => {
  submitting.value = true
  store.dispatch('estimates/createEstimate', estimate.value)
      .then((response) => {
        window.toastr['success'](i18n.global.t('notification.estimate_created'))
        router.push({ name: 'estimates-view', params: { id: response.data.data.id } })
      })
      .catch(err => {
      }).finally(() => submitting.value = false)
}

const updateEstimate = () => {
  submitting.value = true
  store.dispatch('estimates/updateEstimate', estimate.value)
      .then((response) => {
        window.toastr['success'](i18n.global.t('notification.estimate_updated'))
        router.push({ name: 'estimates-view', params: { id: response.data.id } })
      })
      .catch(err => {
      }).finally(() => submitting.value = false)
}

const loadcurrentEstimate = async () => {
  if(route.params.id){
    await store.dispatch('estimates/getEstimateById', route.params.id)
  }
}

function loadData() {
  Promise.all([
      store.dispatch('contacts/loadContacts'),
      store.dispatch('taxes/loadTaxes'),
      store.dispatch('products/loadAllProductSale'),
      store.dispatch('currencies/loadCurrencies'),
      store.dispatch('estimates/loadEstimateStatus'),
      store.dispatch('company/generalInformation'),
      store.dispatch('payments/loadPaymentConditions'),
      store.dispatch('payments/loadPaymentTypes'),
      store.dispatch('countries/loadCountries'),
      loadcurrentEstimate()
  ]).finally(() => {
    isLoading.value = false
    if(route.query.products){
      let products = JSON.parse(route.query.products)
      if(products.length !== 0){
        estimate.value.items = products?.map(el => {
          return {
            product_id: el.id,
            quantity: el.quantity,
            price: el.price,
            description: '',
            discount_type: '',
            discount_value: 0,
            taxes: el.taxes,
          }
        })
      }
    }

    if(route.query.contact_id){
      estimate.value.contact_id = route.query.contact_id
      let selectedContact = contacts.value.find(contact => contact.id === route.query.contact_id);
      if (selectedContact && selectedContact.currency_id) {
        estimate.value.currency_id = selectedContact.currency_id;
      }
    }


    if(route.query.opportunity_id){
      estimate.value.opportunity_id =  route.query.opportunity_id
    }
  })
}

onMounted(() => {
  loadData()
})

</script>

<style scoped>

</style>